import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { SQStep } from '@belong/types';
import { stepEnumToQuery, stepQueryToEnum } from './types';

/**
 * @param newStep `step` query param value we want to set
 * @param replace When `true`: will replace the current browser history entry
 */
export type SetStep = (newStep: SQStep, replace?: boolean) => void;

/**
 * `useStep` hook returns a tuple with step name and setter.
 */
export type UseStep = () => [SQStep, SetStep];

/**
 * Used to manage the current step in the service qualification user flow. Is
 * managed via the `step` query param. Returns two things:
 *
 * 1. the current step name (or null if no query param is found), and
 * 2. a setter which will update the current step to a new value when called
 *
 * The setter function can be called with an optional boolean which, when true,
 * replaces the current browser history entry instead of pushing to it.
 *
 * @example
 *
 * // URL is `/go/internet/check-your-address`
 *
 * const [step, setStep] = useStep();
 * console.log(step); // => null
 * setStep('foobar');
 *
 * // URL is now `/go/internet/check-your-address?step=foobar`
 */
export const useStep: UseStep = () => {
  const router = useRouter();

  const setStep = useCallback<SetStep>(
    (step: SQStep, replace = false) => {
      const { asPath } = router;

      // The hash should only be preserved if we're not on the address look up step
      const hash = step && step !== SQStep.ADDRESS_LOOKUP ? asPath.split('#')[1] : undefined;

      /** (without the current ?query=params) */
      let currentPath = asPath.split('?')[0];

      /** (without the current #hash ) */
      // eslint-disable-next-line prefer-destructuring
      currentPath = currentPath.split('#')[0];

      const newQuery = { ...router.query };
      delete newQuery.slug;
      delete newQuery.step;
      const newStep = stepEnumToQuery(step);
      if (newStep) {
        Object.assign(newQuery, {
          step: newStep
        });
      }

      if (replace) {
        router.replace(
          {
            pathname: currentPath,
            query: newQuery
          },
          undefined,
          { scroll: false, shallow: true }
        );
      } else {
        router.push(
          {
            pathname: currentPath,
            query: newQuery,
            hash
          },
          undefined,
          { scroll: false, shallow: true }
        );
      }
    },
    [router]
  );
  return [stepQueryToEnum(router.query.step ?? null), setStep];
};
